"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.createSocialEditDialog = createSocialEditDialog;
var _dialog = require("../../ui/dialog");
var _litHtml = require("lit-html");
require("../../styles/ContactInfoEditDialog.css");
require("../../styles/EditDialogs.css");
var _rowState = require("../shared/rowState");
var _textUtils = require("../../textUtils");
var _mutations = require("./mutations");
var _profileIcons = require("../../icons-svg/profileIcons");
var _texts = require("../../texts");
var _constants = require("./constants");
var _helpers = require("./helpers");
/* The code below for handling reordering were generated by AI Model: GPT-5.3-Codex. */
/* Prompt: can you implement on SocialEditDialog add the bentoIcon as the first item 
on the row, you should be able to drag this button around and it should allow you 
to change the order of the rows and that should change the order in the mutations 
that happen for this data. */

function sanitizeSocialFieldValue(value) {
  return (0, _textUtils.sanitizeTextValue)(value);
}
function sanitizeUrlFieldValue(value) {
  return (0, _textUtils.sanitizeTextValue)(value).replace(/\s+/g, '');
}
function isValidProfileUrl(value) {
  const normalized = (value || '').trim();
  if (!normalized) return false;
  try {
    const parsed = new URL(normalized);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}
function rowHasContent(row) {
  // A social account is considered meaningful only when a personal profile URL is set.
  return (0, _textUtils.hasNonEmptyText)(row.homepage);
}
function toFormState(socialAccounts) {
  const rows = (socialAccounts || []).map(account => ({
    name: sanitizeSocialFieldValue((0, _textUtils.toText)(account.name)),
    icon: sanitizeSocialFieldValue((0, _textUtils.toText)(account.icon)),
    homepage: sanitizeSocialFieldValue((0, _textUtils.toText)(account.homepage)),
    entryNode: (0, _textUtils.toText)(account.entryNode),
    status: (0, _textUtils.toText)(account.entryNode) ? 'existing' : 'new'
  })).filter(row => Boolean(row.name || row.icon || row.homepage || row.entryNode));
  return {
    socialAccounts: rows.length ? rows : [{
      name: '',
      icon: '',
      homepage: '',
      entryNode: '',
      status: 'new'
    }],
    initialExistingOrder: []
  };
}
function hasOrderChanged(rows, initialExistingOrder) {
  const currentExistingOrder = rows.filter(row => row.status !== 'deleted' && Boolean((row.entryNode || '').trim())).map(row => (row.entryNode || '').trim());
  if (currentExistingOrder.length !== initialExistingOrder.length) return true;
  for (let i = 0; i < currentExistingOrder.length; i++) {
    if (currentExistingOrder[i] !== initialExistingOrder[i]) return true;
  }
  return false;
}
function validateSocialBeforeSave(rows, initialExistingOrder) {
  const ops = (0, _rowState.summarizeRowOps)(rows, rowHasContent);
  const hasChanges = ops.create.length > 0 || ops.update.length > 0 || ops.remove.length > 0;
  const orderChanged = hasOrderChanged(rows, initialExistingOrder);
  if (!hasChanges && !orderChanged) return 'No social changes detected.';
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (!row || row.status === 'deleted') continue;
    const hasAnyValue = [row.name, row.icon, row.homepage].some(_textUtils.hasNonEmptyText);
    if (!hasAnyValue) continue;
    if (!(0, _textUtils.hasNonEmptyText)(row.name) || !(0, _textUtils.hasNonEmptyText)(row.icon)) {
      return `Social account ${i + 1}: please choose an account type from the dropdown.`;
    }
    if (!(0, _textUtils.hasNonEmptyText)(row.homepage)) {
      return `Social account ${i + 1}: please enter your personal profile link.`;
    }
    if (!isValidProfileUrl(row.homepage || '')) {
      return `Social account ${i + 1}: profile link must be a valid http(s) URL.`;
    }
  }
  return null;
}
function renderSocialAccountInputSelect(row, rowIndex, options, onChange) {
  const selected = (0, _helpers.findSocialAccountOption)(options, row?.name || '');
  const selectedLabel = selected?.label || '';
  return (0, _litHtml.html)`
    <select
      class="profile-edit-dialog__social-account-select"
      name=${`social-account-type-${rowIndex}`}
      data-row-index=${String(rowIndex)}
      autocomplete="off"
      @change=${onChange}
    >
      <option value="">Select</option>
      ${options.map(option => (0, _litHtml.html)`
        <option value=${option.label} ?selected=${option.label === selectedLabel}>${option.label}</option>
      `)}
    </select>
  `;
}
function renderSocialInputRow({
  rows,
  index,
  displayIndex,
  onDelete,
  onChange,
  options,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  isDropTarget
}) {
  const row = rows[index];
  const nameLabel = `Social Account Name ${displayIndex + 1}`;
  const homepageLabel = `Social Account Homepage ${displayIndex + 1}`;
  const handleTextInput = field => event => {
    const target = event.target;
    const nextValue = field === 'homepage' ? sanitizeUrlFieldValue(target.value) : sanitizeSocialFieldValue(target.value);
    if (rows[index]) {
      (0, _rowState.applyRowFieldChange)(rows[index], field, nextValue, rowHasContent);
      onChange();
    }
  };
  const handleAccountTypeInput = event => {
    const target = event.target;
    const selected = (0, _helpers.findSocialAccountOption)(options, target.value);
    if (!rows[index]) return;
    if (!selected) {
      (0, _rowState.applyRowFieldChange)(rows[index], 'name', '', rowHasContent);
      (0, _rowState.applyRowFieldChange)(rows[index], 'icon', '', rowHasContent);
      onChange();
      return;
    }
    (0, _rowState.applyRowFieldChange)(rows[index], 'name', selected.label, rowHasContent);
    (0, _rowState.applyRowFieldChange)(rows[index], 'icon', selected.icon || _constants.DEFAULT_ICON_URI, rowHasContent);
    onChange();
  };
  const handleDelete = event => {
    event.preventDefault();
    onDelete();
  };
  return (0, _litHtml.html)`
    <div
      class="profile-edit-dialog__row profile-edit-dialog__row--social ${isDropTarget ? 'profile-edit-dialog__row--drop-target' : ''}"
      @dragover=${event => onDragOver(event)}
      @drop=${() => onDrop(index)}
    >
      <button
        type="button"
        class="profile-edit-dialog__drag-handle"
        aria-label=${`Reorder social account ${displayIndex + 1}`}
        title="Drag to reorder"
        draggable="true"
        @dragstart=${() => onDragStart(index)}
        @dragend=${() => onDragEnd()}
      >
        ${_profileIcons.bentoIcon}
      </button>
      <img 
        class="profile-edit-dialog__social-icon" 
        src="${row?.icon || _constants.DEFAULT_ICON_URI}" 
        alt="${row?.name || 'Social'} icon"
        width="30"
        height="30"
        loading="lazy"
      />
      <label aria-label=${nameLabel} class="label profile-edit-dialog__field profile-edit-dialog__field--social-type">
        ${renderSocialAccountInputSelect(row, index, options, handleAccountTypeInput)}
      </label>
      <label aria-label=${homepageLabel} class="label profile-edit-dialog__field profile-edit-dialog__field--social-url">
        <input
          class="input"
          type="url"
          name=${`social-homepage-${index}`}
          .value=${row?.homepage || ''}
          data-contact-field="homepage"
          data-entry-node=${row?.entryNode || ''}
          data-row-status=${row?.status || 'n/a'}
          placeholder="Profile URL"
          autocomplete="url"
          inputmode="url"
          required
          @input=${handleTextInput('homepage')}
        />
          <small class="profile-edit-dialog__input-help-text">Paste your full profile URL (for example: https://example.com/username)</small>
      </label>
      <div class="profile-edit-dialog__actions profile-edit-dialog__actions--edge">
        <button
          type="button"
          class="profile-edit-dialog__delete-button"
          aria-label=${`Delete social account ${displayIndex + 1}`}
          title=${_texts.deleteEntryButtonTitleText}
          @click=${handleDelete}
        >
          <span class="profile-edit-dialog__delete-icon" aria-hidden="true">${_profileIcons.trashIcon}</span>
        </button>
      </div>
    </div>
  `;
}
function renderSocialSection(rows, options, onRerender) {
  let dragSourceIndex = null;
  let dropTargetIndex = null;
  const reorderRows = (from, to) => {
    if (from === to) return;
    const row = rows[from];
    if (!row) return;
    rows.splice(from, 1);
    rows.splice(to, 0, row);
  };
  const handleDragStart = index => {
    dragSourceIndex = index;
    dropTargetIndex = index;
  };
  const handleDragOver = event => {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
  };
  const handleDrop = index => {
    if (dragSourceIndex === null) return;
    reorderRows(dragSourceIndex, index);
    dragSourceIndex = null;
    dropTargetIndex = null;
    onRerender();
  };
  const handleDragEnd = () => {
    dragSourceIndex = null;
    dropTargetIndex = null;
  };
  const visibleRows = rows.map((row, index) => ({
    row,
    index
  })).filter(({
    row
  }) => row.status !== 'deleted');
  return (0, _litHtml.html)`
    <section class="profile-edit-dialog__section" aria-label="Social Accounts">
      <fieldset>
        <legend class="sr-only">Social account entries</legend>
        ${visibleRows.map(({
    index
  }, displayIndex) => renderSocialInputRow({
    rows,
    index,
    displayIndex,
    onDelete: () => {
      (0, _rowState.deleteRow)(rows, index);
      onRerender();
    },
    onChange: onRerender,
    options,
    onDragStart: handleDragStart,
    onDragOver: handleDragOver,
    onDrop: handleDrop,
    onDragEnd: handleDragEnd,
    isDropTarget: dropTargetIndex === index
  }))}
      </fieldset>
    </section>
  `;
}
function focusSocialField(form, selector) {
  const nextField = form.querySelector(selector);
  if (!nextField || typeof nextField.focus !== 'function') return;
  nextField.scrollIntoView({
    block: 'start',
    behavior: 'auto'
  });
  nextField.focus();
}
function renderSocialEditTemplate(form, formState, store, viewerMode, rerenderOptions = {}) {
  const rerender = (nextOptions = {}) => renderSocialEditTemplate(form, formState, store, viewerMode, nextOptions);
  const socialOptions = (0, _helpers.getSocialAccountOptions)(store);
  (0, _litHtml.render)((0, _litHtml.html)`
    ${renderSocialSection(formState.socialAccounts, socialOptions, rerender)}
    ${viewerMode !== 'owner' ? (0, _litHtml.html)`<p class="profile-edit-dialog__login-message">${_texts.ownerLoginRequiredDialogMessageText}</p>` : null}
  `, form);
  if (rerenderOptions.focusSelector) {
    focusSocialField(form, rerenderOptions.focusSelector);
  }
}
function createSocialEditForm(details, store, viewerMode) {
  const form = document.createElement('form');
  form.classList.add('profile__edit-form');
  const formState = toFormState(details);
  formState.initialExistingOrder = formState.socialAccounts.filter(row => Boolean((row.entryNode || '').trim())).map(row => (row.entryNode || '').trim());
  renderSocialEditTemplate(form, formState, store, viewerMode);
  const addRow = () => {
    formState.socialAccounts.unshift({
      name: '',
      icon: '',
      homepage: '',
      entryNode: '',
      status: 'new'
    });
    renderSocialEditTemplate(form, formState, store, viewerMode, {
      focusSelector: '[name="social-account-type-0"]'
    });
  };
  return {
    form,
    formState,
    addRow
  };
}
async function createSocialEditDialog(event, store, subject, socialAccounts, viewerMode, onSaved) {
  const dom = event.currentTarget?.ownerDocument || document;
  const {
    form,
    formState,
    addRow
  } = createSocialEditForm(socialAccounts, store, viewerMode);
  const result = await (0, _dialog.openInputDialog)({
    title: _texts.editSocialDialogTitleText,
    dom,
    form,
    headerAction: {
      type: 'button',
      label: '+ Add More',
      ariaLabel: 'Add another social account',
      onClick: addRow
    },
    submitLabel: _texts.dialogSubmitLabelText,
    cancelLabel: _texts.dialogCancelLabelText,
    validate: () => {
      if (viewerMode !== 'owner') {
        return _texts.ownerLoginRequiredDialogMessageText;
      }
      return validateSocialBeforeSave(formState.socialAccounts, formState.initialExistingOrder);
    },
    onSave: async () => {
      const socialOps = (0, _rowState.summarizeRowOps)(formState.socialAccounts, rowHasContent);
      const plan = {
        create: socialOps.create,
        update: socialOps.update,
        remove: socialOps.remove
      };
      await (0, _mutations.processSocialMutations)(store, subject, plan, formState.socialAccounts);
    },
    formatSaveError: error => {
      const message = error instanceof Error ? error.message : String(error);
      return message.startsWith(_texts.saveSocialUpdatesFailedPrefixText) ? message : `${_texts.saveSocialUpdatesFailedPrefixText} ${message}`;
    }
  });
  if (!result) return;
  if (onSaved) {
    await onSaved();
  }
}